import Vue from "vue";
import { formatDate_MDYHMS } from "@/_core/_helpers/_helpers";
import { gatewayService } from "@/_services/gateway.service";

const state = {
  all: {},
  ai_chat_messages: [],
};

const actions = {
  // Fetch all preferences via the gateway (migrated off GraphQL 2026-07-15)
  // TODO: verificar endpoint — response shape assumed to mirror the old
  // GraphQL overview_choices field (flat object with the 6 *_choices keys);
  // not confirmed against a live backend.
  async getAll({ commit }) {
    commit("getAllRequest");
    await gatewayService.user
      .overviewChoices({
        format: "json",
        user_id: parseInt(localStorage.getItem("userData")),
        refresh: formatDate_MDYHMS(new Date())
      })
      .then(data => {
        commit("getAllSuccess", data);
      })
      .catch(error => {
        console.log(error);
        commit("getAllFailure", error);
      });
  },
  // New action to update overview_choices
  updateOverviewChoices({ commit }, overview_choices) {
    commit("setOverviewChoices", overview_choices);
  },

  // Update all news preferences inside overview_choices
  updateNewsPreferences({ commit }, preferences) {
    commit("setNewsPreferences", preferences);
  },

  // New action to update analytics_choices
  updateAnalyticsChoices({ commit }, analytics_choices) {
    commit("setAnalyticsChoices", analytics_choices);
  },

  // Update selected_nav across multiple preferences
  updateSelectedNav({ commit }, selected_nav) {
    commit("setSelectedNav", selected_nav);
  },

  // Update color_theme across multiple preferences
  updateColorTheme({ commit }, color_theme) {
    commit("setColorTheme", color_theme);
  },

  updateAiChatMessages({ commit }, messages) {
    commit("setAiChatMessages", messages);
  },

  logout({ commit }) {
    commit("logout");
  }
};

const mutations = {
  getAllRequest(state) {
    state.all = { loading: true };
  },
  getAllSuccess(state, user_preferences) {
    state.all = { items: user_preferences };
  },
  getAllFailure(state, error) {
    state.all = { error };
  },

  // New mutation to update overview_choices
  setOverviewChoices(state, overview_choices) {
    // Ensure items exists
    if (!state.all.items) {
      Vue.set(state.all, "items", {});
    }
    // Set overview_choices with Vue.set for reactivity
    Vue.set(state.all.items, "overview_choices", overview_choices);
  },

  // Mutation to update all news preferences as a single object inside overview_choices.news
  setNewsPreferences(state, newsPreferences) {
    if (!state.all.items) {
      Vue.set(state.all, "items", {});
    }
    if (!state.all.items.overview_choices) {
      Vue.set(state.all.items, "overview_choices", {});
    }
    Vue.set(state.all.items.overview_choices, "news", newsPreferences);
  },

  // New mutation to update analytics_choices
  setAnalyticsChoices(state, analytics_choices) {
    // Ensure items exists
    if (!state.all.items) {
      Vue.set(state.all, "items", {});
    }
    state.all.items.analytics_choices = analytics_choices;
  },

  // Update selected_nav
  setSelectedNav(state, selected_nav) {
    if (!state.all.items) {
      Vue.set(state.all, "items", {});
    }
    const categories = [
      "analytics_choices",
      "datamodel_choices",
      "markets_choices",
      "optimizer_choices"
    ];
    categories.forEach(category => {
      if (!state.all.items[category]) {
        Vue.set(state.all.items, category, {});
      }
      Vue.set(state.all.items[category], "selected_nav", selected_nav);
    });
  },

  // Update color_theme
  setColorTheme(state, color_theme) {
    if (!state.all.items) {
      Vue.set(state.all, "items", {});
    }
    const categories = [
      "analytics_choices",
      "datamodel_choices",
      "hedges_choices",
      "markets_choices",
      "optimizer_choices",
      "overview_choices"
    ];
    categories.forEach(category => {
      if (!state.all.items[category]) {
        Vue.set(state.all.items, category, {});
      }
      Vue.set(state.all.items[category], "color_theme", color_theme);
    });
  },

  setAiChatMessages(state, messages) {
    state.ai_chat_messages = messages;
  },

  logout(state) {
    state.all = null;
    state.ai_chat_messages = [];
  }
};

// Optional: Getter for cleaner access
const getters = {
  overviewChoices: state => state.all?.items?.overview_choices,
  analyticsChoices: state => state.all?.items?.analytics_choices,
  datamodelChoices: state => state.all?.items?.datamodel_choices,
  marketsChoices: state => state.all?.items?.markets_choices,
  optimizerChoices: state => state.all?.items?.optimizer_choices,
  hedgesChoices: state => state.all?.items?.hedges_choices,
  // News preferences — all under overview_choices.news
  newsPreferences: state => state.all?.items?.overview_choices?.news,
  newsCommodities: state => state.all?.items?.overview_choices?.news?.commodities,
  newsCurrencies: state => state.all?.items?.overview_choices?.news?.currencies,
  newsShowMacro: state => state.all?.items?.overview_choices?.news?.show_macro,
  newsShowIndexes: state => state.all?.items?.overview_choices?.news?.show_indexes,
  newsShowIntRates: state => state.all?.items?.overview_choices?.news?.show_int_rates,
  aiChatMessages: state => state.ai_chat_messages || [],
};

export const user_preferences = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
